import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { NmiPayments } from '@nmipayments/nmi-pay-react';
import { Box, ButtonBase, Checkbox, Collapse, Link, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { post } from '../../api';
import { getStateCodeByName, getStateFeeByCode, getStateNameByCode, getEntityRenewalPrice, getEntityRenewalLabel, PRIVACY_POLICY, STATE_OPTIONS_BY_NAME, TERMS_AND_CONDITIONS } from './dummyData';
import { getCardExpiryFromLookup, getCardLast4FromLookup, getNMIConfigSummary, getNmiAppearance, isNMIHostedEnabled } from './nmiGateway';
import { useOnlineCart } from './OnlineCartContext';
import { encryptOnlineCartAnswersForService } from './ssnPayload';
import { ONLINE_CART_TOKENS } from './style';
import { getOrderSummaryValidationErrors, scrollToFieldAndFocus } from './validation';

function formatPrice(value) {
    if (value === 0) {
        return 'Free';
    }
    const num = Number(value || 0);
    // Drop the cents for whole amounts ("$72"), keep them when there's a fractional part ("$72.50").
    return Number.isInteger(num) ? `$${num}` : `$${num.toFixed(2)}`;
}

const priceLabelFor = (opt) => opt?.priceDisplay || formatPrice(Number(opt?.price) || 0);

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Pulls a trailing unit suffix off a price display string so a discounted amount can carry the
// same one: "$80/yr" -> "/yr", "$1,200.00 / mo" -> " / mo", "$80" -> "". Whitespace before the
// suffix is preserved so the separator stays intact.
const extractPriceSuffix = (display) => {
    const match = String(display == null ? '' : display).match(/^\s*\$?\s*[\d,]+(?:\.\d+)?(.*)$/);
    return match ? match[1] : '';
};

// Sentinel id a Product-type promo uses to target the Entity Annual Renewal (PRIME LLC
// Formation) base amount, which is not a Cart_Product line item. Mirrors the backend.
const ENTITY_RENEWAL_DISCOUNT_ID = '__entity_annual_renewal__';

// Order-summary price cell. When a Product-type promo discounts this line, the list price is
// struck through and the discounted (what-you-pay-today) price is shown in green beneath it.
// Without a discount it renders the price exactly as before. Exported for unit testing.
export function PriceCell({ original, originalDisplay, discountPercent }) {
    const numericOriginal = Number(original) || 0;
    const pct = Math.max(0, Math.min(100, Number(discountPercent) || 0));
    const display = originalDisplay || formatPrice(numericOriginal);

    if (pct <= 0 || numericOriginal <= 0) {
        return (
            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textHeading, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>
                {display}
            </Typography>
        );
    }

    const discounted = roundCurrency(numericOriginal * (1 - pct / 100));
    // Carry any unit suffix from the list price ("$80/yr" -> "/yr") onto the discounted amount,
    // but not onto a fully-discounted "Free" value.
    const suffix = extractPriceSuffix(display);
    const discountedDisplay = discounted > 0 ? `${formatPrice(discounted)}${suffix}` : formatPrice(discounted);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.6, minWidth: '60px' }}>
            <Typography component="span" sx={{ fontSize: '0.68rem', color: ONLINE_CART_TOKENS.textMuted, fontWeight: 500, textDecoration: 'line-through' }}>
                {display}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textHeading, fontWeight: 700 }}>
                {discountedDisplay}
            </Typography>
        </Box>
    );
}

// "+ Add <product>" affordance in the order summary. A product with a single paid option adds on
// click; one with multiple paid options expands to an inline picker so the customer chooses.
function AddableProduct({ productName, paidOptions, fieldId, onSelect }) {
    const [open, setOpen] = useState(false);
    const headlineOption = paidOptions.find((o) => o.isRecommended) || paidOptions[0];

    if (paidOptions.length <= 1) {
        return (
            <ButtonBase
                onClick={() => onSelect(fieldId, headlineOption.optionCode || headlineOption.id)}
                sx={{
                    mt: 0.5,
                    p: 1.2,
                    borderRadius: 1.5,
                    border: `1px dashed ${ONLINE_CART_TOKENS.primaryBorderDashed}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: ONLINE_CART_TOKENS.primary,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                }}
            >
                <span>+ Add {productName}</span>
                <span>{priceLabelFor(headlineOption)}</span>
            </ButtonBase>
        );
    }

    return (
        <Box sx={{ mt: 0.5, borderRadius: 1.5, border: `1px dashed ${ONLINE_CART_TOKENS.primaryBorderDashed}`, overflow: 'hidden' }}>
            <ButtonBase
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    width: '100%',
                    p: 1.2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: ONLINE_CART_TOKENS.primary,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                }}
            >
                <span>+ Add {productName}</span>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {priceLabelFor(headlineOption)}
                    {open ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                </Box>
            </ButtonBase>
            <Collapse in={open}>
                <Box sx={{ px: 1, pb: 1, display: 'grid', rowGap: 0.5 }}>
                    {paidOptions.map((opt) => (
                        <ButtonBase
                            key={opt.optionCode || opt.id}
                            onClick={() => onSelect(fieldId, opt.optionCode || opt.id)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                p: 1,
                                borderRadius: 1,
                                textAlign: 'left',
                                '&:hover': { backgroundColor: ONLINE_CART_TOKENS.primaryBgSoft },
                            }}
                        >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                {opt.isRecommended ? (
                                    <RadioButtonCheckedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.primary }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: ONLINE_CART_TOKENS.textPlaceholder }} />
                                )}
                                <Typography component="span" sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong, fontWeight: 500 }}>
                                    {opt.label}
                                </Typography>
                                {opt.isRecommended ? (
                                    <Typography
                                        component="span"
                                        sx={{ ml: 0.25, px: 0.75, py: 0.15, borderRadius: 1, fontSize: '0.65rem', fontWeight: 700, color: '#fff', backgroundColor: ONLINE_CART_TOKENS.accentOrange, whiteSpace: 'nowrap' }}
                                    >
                                        Popular
                                    </Typography>
                                ) : null}
                            </Box>
                            <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeading, whiteSpace: 'nowrap' }}>
                                {priceLabelFor(opt)}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

export default function ReviewOrder({
    value,
    onChange,
    error,
    onPaymentProceed,
    onFieldChange,
    allAnswers,
    TextQuestionField,
    BasicSelectQuestionField,
    FieldError,
}) {
    const { recordId, onlineCartRecord } = useOnlineCart();
    const cartProducts = useMemo(() => onlineCartRecord?.products || [], [onlineCartRecord]);
    const [showPromo, setShowPromo] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(true);
    const [showErrors, setShowErrors] = useState(false);
    const [promoFeedback, setPromoFeedback] = useState({ type: '', message: '' });
    const [promoApplying, setPromoApplying] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [nmiFieldsReady, setNmiFieldsReady] = useState(false);

    const safeAnswers = useMemo(() => allAnswers || {}, [allAnswers]);
    const defaultCardholderName = `${safeAnswers.firstName || ''} ${safeAnswers.lastName || ''}`.trim();
    const stateFormation = safeAnswers.stateOfFormation || null;
    const stateCode = stateFormation?.stateCode || '';
    const stateLabel = stateFormation?.stateName || getStateNameByCode(stateCode, 'State');
    const stateFee = Number.isFinite(Number(stateFormation?.stateFee)) ? Number(stateFormation.stateFee) : getStateFeeByCode(stateCode, 0);

    const checkout = {
        cardholderName: value?.cardholderName ?? defaultCardholderName,
        cardNumber: value?.cardNumber || '',
        expiry: value?.expiry || '',
        cvv: value?.cvv || '',
        billingSameAsMailing: value?.billingSameAsMailing ?? true,
        billingAddress1: value?.billingAddress1 || '',
        billingAddress2: value?.billingAddress2 || '',
        billingCity: value?.billingCity || '',
        billingState: value?.billingState || '',
        billingStateCode: value?.billingStateCode || '',
        billingZip: value?.billingZip || '',
        promoCode: value?.promoCode || '',
        promoApplied: value?.promoApplied || false,
        promoDiscount: value?.promoDiscount || 0,
        promoId: value?.promoId || '',
        promoName: value?.promoName || '',
        promoDescription: value?.promoDescription || '',
        promoDiscountInAmount: value?.promoDiscountInAmount ?? null,
        promoDiscountInPercent: value?.promoDiscountInPercent ?? null,
        promoSubtotal: value?.promoSubtotal ?? null,
        promoTotalDue: value?.promoTotalDue ?? null,
        promoDiscountType: value?.promoDiscountType || '',
        promoDiscountProductIds: value?.promoDiscountProductIds ?? '',
        useNMIHosted: value?.useNMIHosted ?? isNMIHostedEnabled(),
        paymentToken: value?.paymentToken || '',
        cardLast4: value?.cardLast4 || '',
        cardExpiryMonth: value?.cardExpiryMonth || '',
        cardExpiryYear: value?.cardExpiryYear || '',
        nmiLookupData: value?.nmiLookupData || null,
    };
    const nmiSummary = getNMIConfigSummary();
    const paymentAlreadyProcessed = Boolean(allAnswers?.paymentProcessed);

    const validationErrors = showErrors ? getOrderSummaryValidationErrors(checkout) : {};

    const isRegisteredAgentAdded = safeAnswers.registeredAgent === 'add_registered_agent';
    const isExpeditedAdded = safeAnswers.expeditedProcessing === 'expedited';
    const isRelayAdded = safeAnswers.relayBank === 'relay_yes';

    const hasDynamicProducts = cartProducts.length > 0;

    const selectedProductLineItems = useMemo(() => {
        if (!hasDynamicProducts) return [];
        return cartProducts
            .filter((p) => p && p.isActive !== false)
            .slice()
            .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0))
            .map((product) => {
                const stableKey = product.productCode || product.id;
                const fieldId = stableKey ? `cartProductSelection_${stableKey}` : '';
                const options = product.options || [];
                const selectedValue = fieldId ? safeAnswers?.[fieldId] : null;
                const selectedOption = options.find((o) => (o.optionCode || o.id) === selectedValue);
                if (!selectedOption) return null;
                const price = Number(selectedOption.price) || 0;
                if (price <= 0) return null;
                // "Remove" switches to the product's decline/$0 option (or clears it when optional).
                const declineOption = options.find((o) => o.isDeclineOption) || options.find((o) => (Number(o.price) || 0) === 0);
                const declineValue = declineOption ? declineOption.optionCode || declineOption.id : '';
                return { product, option: selectedOption, price, fieldId, declineValue, canRemove: Boolean(declineValue) || !product.isRequired };
            })
            .filter(Boolean);
    }, [cartProducts, hasDynamicProducts, safeAnswers]);

    // Active dynamic products with no paid option selected — surfaced as "+ Add" buttons.
    const addableDynamicProducts = useMemo(() => {
        if (!hasDynamicProducts) return [];
        return cartProducts
            .filter((p) => p && p.isActive !== false)
            .slice()
            .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0))
            .map((product) => {
                const stableKey = product.productCode || product.id;
                const fieldId = stableKey ? `cartProductSelection_${stableKey}` : '';
                const options = (product.options || []).filter((o) => o && o.isActive !== false);
                const selectedValue = fieldId ? safeAnswers?.[fieldId] : null;
                const selectedOption = options.find((o) => (o.optionCode || o.id) === selectedValue);
                if (selectedOption && (Number(selectedOption.price) || 0) > 0) return null;
                const paidOptions = options
                    .filter((o) => (Number(o.price) || 0) > 0)
                    .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));
                if (!paidOptions.length) return null;
                return { product, fieldId, paidOptions };
            })
            .filter(Boolean);
    }, [cartProducts, hasDynamicProducts, safeAnswers]);

    const dynamicProductsSubtotal = selectedProductLineItems.reduce((sum, item) => sum + item.price, 0);
    const legacyProductsSubtotal = (isRegisteredAgentAdded ? 99 : 0) + (isExpeditedAdded ? 49 : 0);
    const productsSubtotal = hasDynamicProducts ? dynamicProductsSubtotal : legacyProductsSubtotal;

    const subtotal = getEntityRenewalPrice() + stateFee + productsSubtotal;
    const totalFromServer = Number(checkout.promoTotalDue);
    const total = checkout.promoApplied && Number.isFinite(totalFromServer) ? Math.max(0, totalFromServer) : Math.max(0, subtotal - (checkout.promoApplied ? checkout.promoDiscount : 0));

    // Product-type promos carry a per-product percentage as JSON ([{ id, percent }], synced from
    // Salesforce). Build an id -> percent lookup so each targeted summary line can show its own
    // discount. `id` is a Cart_Product id (matches product.id) or the entity-renewal sentinel.
    // Only product-type promos drive per-line strikethroughs; amount/percent promos are reflected
    // in the applied-promo card and the total only, exactly as before. A legacy comma-separated
    // id list (no per-product percent) fails JSON.parse and yields an empty map — no strikethrough,
    // matching the backend, which also produces no discount for that legacy shape.
    const productDiscountMap = useMemo(() => {
        const map = new Map();
        if (!checkout.promoApplied || String(checkout.promoDiscountType || '').toLowerCase() !== 'product') {
            return map;
        }
        const raw = checkout.promoDiscountProductIds;
        let entries = [];
        if (Array.isArray(raw)) {
            entries = raw;
        } else if (typeof raw === 'string' && raw.trim()) {
            try {
                const parsed = JSON.parse(raw.trim());
                if (Array.isArray(parsed)) entries = parsed;
            } catch (e) {
                entries = [];
            }
        }
        for (const entry of entries) {
            if (!entry || !entry.id) continue;
            const pct = Math.max(0, Math.min(100, Number(entry.percent) || 0));
            if (pct > 0) map.set(String(entry.id), pct);
        }
        return map;
    }, [checkout.promoApplied, checkout.promoDiscountType, checkout.promoDiscountProductIds]);

    const getPromoResetPatch = (promoCode = '') => ({
        promoCode,
        promoApplied: false,
        promoDiscount: 0,
        promoId: '',
        promoName: '',
        promoDescription: '',
        promoDiscountInAmount: null,
        promoDiscountInPercent: null,
        promoSubtotal: null,
        promoTotalDue: null,
        promoDiscountType: '',
        promoDiscountProductIds: '',
    });

    const updateCheckout = (patch) => {
        onChange({
            ...checkout,
            ...patch,
        });
    };

    const scrollToCheckoutField = (fieldId) => {
        scrollToFieldAndFocus(fieldId, {
            selectorTemplate: '[data-online-cart-checkout-field="{fieldId}"]',
            fallbackToElementId: true,
            focusAnchorIfFocusable: true,
        });
    };

    useEffect(() => {
        if (!error) {
            return;
        }
        setShowErrors(true);
    }, [error]);

    useEffect(() => {
        // Hosted payment fields cannot be restored on remount; clear stale token state for accurate UX.
        if (checkout.useNMIHosted && (checkout.paymentToken || checkout.cardLast4 || checkout.nmiLookupData)) {
            updateCheckout({
                paymentToken: '',
                cardLast4: '',
                cardExpiryMonth: '',
                cardExpiryYear: '',
                nmiLookupData: null,
            });
        }
        setNmiFieldsReady(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!checkout.promoApplied) {
            return;
        }

        const baseMessage = `Code applied. You saved ${formatPrice(checkout.promoDiscount)}.`;
        const detail = String(checkout.promoDescription || '').trim();
        setPromoFeedback({ type: 'success', message: detail ? `${baseMessage} ${detail}` : baseMessage });
    }, [checkout.promoApplied, checkout.promoDiscount, checkout.promoDescription]);

    const handleApplyPromo = async () => {
        const code = String(checkout.promoCode || '')
            .trim()
            .toUpperCase();
        if (!code) {
            setPromoFeedback({ type: 'error', message: 'Please enter a promo code.' });
            return;
        }
        if (!recordId) {
            setPromoFeedback({ type: 'error', message: 'Cart context is missing. Please refresh and try again.' });
            return;
        }

        setPromoApplying(true);
        setPromoFeedback({ type: '', message: '' });

        try {
            const answersForService = await encryptOnlineCartAnswersForService(safeAnswers);
            const result = await post('onlineCart/applyPromocode', {
                recordId,
                promoCode: code,
                answers: answersForService,
            });

            if (!result?.isSuccess) {
                updateCheckout(getPromoResetPatch(code));
                setPromoFeedback({ type: 'error', message: result?.errorMessage || 'Invalid promo code. Please try again.' });
                return;
            }

            const promo = result?.data?.promo || {};
            const summary = result?.data?.summary || {};
            const promoDiscount = Number(summary?.discountAmount || 0);
            if (promoDiscount <= 0) {
                updateCheckout(getPromoResetPatch(code));
                setPromoFeedback({ type: 'error', message: 'Invalid promo code. Please try again.' });
                return;
            }

            const promoDescription = String(promo?.description || '').trim();
            const nextPromoCode = promo?.promoCode || code;
            updateCheckout({
                promoCode: nextPromoCode,
                promoApplied: true,
                promoDiscount,
                promoId: promo?.id || '',
                promoName: promo?.name || '',
                promoDescription,
                promoDiscountInAmount: promo?.discountInAmount ?? null,
                promoDiscountInPercent: promo?.discountInPercent ?? null,
                promoSubtotal: Number.isFinite(Number(summary?.subtotal)) ? Number(summary.subtotal) : null,
                promoTotalDue: Number.isFinite(Number(summary?.totalDue)) ? Number(summary.totalDue) : null,
                promoDiscountType: promo?.discountType || '',
                promoDiscountProductIds: promo?.discountProductIds ?? '',
            });

            const baseMessage = `Code applied. You saved ${formatPrice(promoDiscount)}.`;
            setPromoFeedback({
                type: 'success',
                message: promoDescription ? `${baseMessage} ${promoDescription}` : baseMessage,
            });
        } catch (error) {
            updateCheckout(getPromoResetPatch(code));
            setPromoFeedback({ type: 'error', message: error?.message || 'Unable to apply promo code right now. Please try again.' });
        } finally {
            setPromoApplying(false);
        }
    };

    const handleRemovePromo = () => {
        updateCheckout(getPromoResetPatch(''));
        setPromoFeedback({ type: '', message: '' });
        setShowPromo(false);
    };

    const handleContinue = async () => {
        setPaymentError('');
        setPaymentProcessing(true);
        setShowErrors(true);
        const freshErrors = getOrderSummaryValidationErrors(checkout);
        const firstErrorFieldOrder = ['cardholderName', 'paymentToken', 'cardNumber', 'expiry', 'cvv', 'billingAddress1', 'billingCity', 'billingState', 'billingZip'];
        const firstErrorFieldId = firstErrorFieldOrder.find((fieldId) => Boolean(freshErrors[fieldId])) || Object.keys(freshErrors)[0] || '';
        if (Object.keys(freshErrors).length > 0) {
            setPaymentError('');
            scrollToCheckoutField(firstErrorFieldId);
            setPaymentProcessing(false);
            return;
        }
        if (checkout.useNMIHosted) {
            if (!nmiSummary.tokenizationKeyConfigured) {
                setPaymentError('NMI tokenization key is missing. Configure REACT_APP_NMI_TOKENIZATION_KEY.');
                setPaymentProcessing(false);
                return;
            }
            if (!checkout.paymentToken) {
                setPaymentError('Please complete your card details before continuing.');
                setPaymentProcessing(false);
                return;
            }
            if (!nmiFieldsReady) {
                setPaymentError('Secure payment fields are still loading. Please wait a moment and retry.');
                setPaymentProcessing(false);
                return;
            }
        }

        try {
            const response = await onPaymentProceed?.();
            if(!response?.isSuccess){
                throw Error(response?.error || 'Payment failed. Please try again.')
            }
        } catch (err) {
            setPaymentError(err?.message || 'Payment failed. Please try again.');
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handleNmiChange = (event) => {
        const token = String(event?.token || '');
        const complete = Boolean(event?.complete && token);
        const cardLast4 = complete ? getCardLast4FromLookup(event?.lookupData) : '';
        const cardExpiry = complete ? getCardExpiryFromLookup(event?.lookupData) : { expiryMonth: '', expiryYear: '' };

        updateCheckout({
            paymentToken: complete ? token : '',
            cardLast4,
            cardExpiryMonth: cardExpiry.expiryMonth || '',
            cardExpiryYear: cardExpiry.expiryYear || '',
            nmiLookupData: complete ? event?.lookupData || null : null,
            useNMIHosted: true,
        });

        if (complete) {
            setPaymentError('');
        }
    };

    const handleFieldsAvailable = () => {
        setNmiFieldsReady(true);
    };

    const handleToggleAddon = (type, enabled) => {
        if (!onFieldChange) {
            return;
        }
        if (checkout.promoApplied) {
            updateCheckout(getPromoResetPatch(checkout.promoCode));
            setPromoFeedback({ type: '', message: '' });
        }
        if (type === 'registeredAgent') {
            onFieldChange('registeredAgent', enabled ? 'add_registered_agent' : 'self_handle');
            return;
        }
        if (type === 'expeditedProcessing') {
            onFieldChange('expeditedProcessing', enabled ? 'expedited' : 'standard');
            return;
        }
        if (type === 'relayBank') {
            onFieldChange('relayBank', enabled ? 'relay_yes' : 'relay_no');
            if (enabled && !safeAnswers.relayEmployeeCount) {
                onFieldChange('relayEmployeeCount', 'Just me (no employees)');
            }
            if (!enabled) {
                onFieldChange('relayEmployeeCount', undefined);
                onFieldChange('relayBankDateOfBirth', undefined);
            }
            return;
        }
    };

    const handleSelectDynamicProductOption = (fieldId, optionValue) => {
        if (!onFieldChange || !fieldId) {
            return;
        }
        // Changing the cart invalidates a server-priced promo, so reset it like the legacy add-ons do.
        if (checkout.promoApplied) {
            updateCheckout(getPromoResetPatch(checkout.promoCode));
            setPromoFeedback({ type: '', message: '' });
        }
        onFieldChange(fieldId, optionValue || '');
    };

    const cardBorderStyle = {
        border: `1px solid ${ONLINE_CART_TOKENS.border}`,
        borderRadius: 3,
        backgroundColor: ONLINE_CART_TOKENS.surface,
        p: { xs: 2, sm: 2.5 },
    };

    return (
        <Box sx={{ mt: 0 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '2.6fr 2.4fr' },
                    gap: 2.5,
                    '& button.MuiButtonBase-root:focus-visible': {
                        outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                        outlineOffset: '2px',
                    },
                }}
            >
                <Box sx={{ ...cardBorderStyle, order: { xs: 2, md: 1 } }}>
                    <Typography sx={{ fontSize: { xs: '1rem' }, fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay, display: { xs: 'block' }, mb: 2 }}>Payment Details</Typography>

                    <Box sx={{ display: 'grid', rowGap: 1.75 }}>
                        <TextQuestionField
                            field={{ id: 'cardholderName', label: 'Cardholder Name', placeholder: 'Full name on card', size: 'compact' }}
                            value={checkout.cardholderName}
                            onChange={(v) => updateCheckout({ cardholderName: v })}
                            error={validationErrors.cardholderName}
                            styles={{
                                labelSx: {
                                    fontSize: '0.75rem',
                                },
                            }}
                        />
                        {checkout.useNMIHosted ? (
                            <Box
                                data-online-cart-checkout-field="paymentToken"
                                sx={{
                                    p: 0,
                                }}
                            >
                                {nmiSummary.tokenizationKeyConfigured ? (
                                    <Box
                                        sx={{
                                            '& nmi-pay-widget': {
                                                display: 'block',
                                                width: '100%',
                                                // border: `1px solid ${validationErrors.paymentToken ? ONLINE_CART_TOKENS.error : ONLINE_CART_TOKENS.border}`,
                                                // borderRadius: ONLINE_CART_TOKENS.radiusMd,
                                                // transition: 'border-color 0.2s ease',
                                            },
                                        }}
                                    >
                                        <NmiPayments
                                            tokenizationKey={nmiSummary.tokenizationKey}
                                            paymentMethods={['card']}
                                            layout="multiLine"
                                            appearance={getNmiAppearance()}
                                            onChange={handleNmiChange}
                                            onFieldsAvailable={handleFieldsAvailable}
                                        />
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '0.75rem', color: ONLINE_CART_TOKENS.error, fontWeight: 600 }}>NMI key missing. Set `REACT_APP_NMI_TOKENIZATION_KEY`.</Typography>
                                )}
                                <FieldError error={validationErrors.paymentToken} size="compact" />
                            </Box>
                        ) : (
                            <>
                                <TextQuestionField
                                    field={{ id: 'cardNumber', label: 'Card Number', placeholder: '4242 4242 4242 4242', size: 'compact' }}
                                    value={checkout.cardNumber}
                                    onChange={(v) => updateCheckout({ cardNumber: v })}
                                    error={validationErrors.cardNumber}
                                    styles={{
                                        labelSx: {
                                            fontSize: '0.75rem',
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                    <TextQuestionField
                                        field={{ id: 'expiry', label: 'Expiry', placeholder: 'MM/YY', size: 'compact' }}
                                        value={checkout.expiry}
                                        onChange={(v) => updateCheckout({ expiry: v })}
                                        error={validationErrors.expiry}
                                        styles={{
                                            labelSx: {
                                                fontSize: '0.75rem',
                                            },
                                        }}
                                    />
                                    <TextQuestionField
                                        field={{ id: 'cvv', label: 'CVV', placeholder: '123', size: 'compact' }}
                                        value={checkout.cvv}
                                        onChange={(v) => updateCheckout({ cvv: String(v).replace(/\D/g, '').slice(0, 4) })}
                                        error={validationErrors.cvv}
                                        styles={{
                                            labelSx: {
                                                fontSize: '0.75rem',
                                            },
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                            {['Visa', 'MC', 'Amex', 'Disc'].map((brand) => (
                                <Box
                                    key={brand}
                                    sx={{
                                        px: 0.8,
                                        py: 0.35,
                                        border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                                        borderRadius: 0.75,
                                        fontSize: '0.62rem',
                                        fontWeight: 700,
                                        color: ONLINE_CART_TOKENS.textMeta,
                                    }}
                                >
                                    {brand}
                                </Box>
                            ))}
                        </Box>
                        {checkout.paymentToken ? <Typography sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.textSuccess, fontWeight: 600 }}>Card details secured.</Typography> : null}
                    </Box>

                    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${ONLINE_CART_TOKENS.border}` }}>
                        <Typography sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' }, fontWeight: 700, color: ONLINE_CART_TOKENS.textDisplay, mb: 1.5 }}>Billing Address</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Checkbox
                                checked={Boolean(checkout.billingSameAsMailing)}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    const mailingPrefill = {
                                        billingAddress1: safeAnswers.mailingAddress1 || '',
                                        billingAddress2: safeAnswers.mailingAddress2 || '',
                                        billingCity: safeAnswers.mailingCity || '',
                                        billingState: safeAnswers.mailingState || '',
                                        billingStateCode: safeAnswers.mailingStateCode || '',
                                        billingZip: safeAnswers.mailingZip || '',
                                    };
                                    if (checked) {
                                        updateCheckout({
                                            billingSameAsMailing: true,
                                            ...mailingPrefill,
                                        });
                                        return;
                                    }
                                    const hasBillingAddress = Boolean(checkout.billingAddress1 || checkout.billingAddress2 || checkout.billingCity || checkout.billingState || checkout.billingZip);
                                    updateCheckout({
                                        billingSameAsMailing: false,
                                        ...(hasBillingAddress ? {} : mailingPrefill),
                                    });
                                }}
                                size="small"
                                sx={{ p: 0.25, '&.Mui-checked': { color: ONLINE_CART_TOKENS.primary } }}
                            />
                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textSupport }}>Same as mailing address</Typography>
                        </Box>

                        {checkout.billingSameAsMailing ? (
                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textSupport, lineHeight: 1.6 }}>
                                {safeAnswers.mailingAddress1 || 'Address not provided'}
                                {safeAnswers.mailingAddress2 ? `, ${safeAnswers.mailingAddress2}` : ''}, {safeAnswers.mailingCity || ''}, {safeAnswers.mailingState || stateLabel}{' '}
                                {safeAnswers.mailingZip || ''}
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: 'grid', rowGap: 1.5 }}>
                                    <TextQuestionField
                                        field={{ id: 'billingAddress1', label: 'Street Address', placeholder: '123 Main St', size: 'compact' }}
                                        value={checkout.billingAddress1}
                                        onChange={(v) => updateCheckout({ billingAddress1: v })}
                                        error={validationErrors.billingAddress1}
                                        styles={{
                                            labelSx: {
                                                fontSize: '0.75rem',
                                            },
                                        }}
                                    />
                                    <TextQuestionField
                                        field={{ id: 'billingAddress2', label: 'Apt, Suite, Unit', placeholder: 'Apt 4B', optional: true, size: 'compact' }}
                                        value={checkout.billingAddress2}
                                        onChange={(v) => updateCheckout({ billingAddress2: v })}
                                        styles={{
                                            labelSx: {
                                                fontSize: '0.75rem',
                                            },
                                        }}
                                    />
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                        <TextQuestionField
                                            field={{ id: 'billingCity', label: 'City', placeholder: 'New York', size: 'compact' }}
                                            value={checkout.billingCity}
                                            onChange={(v) => updateCheckout({ billingCity: v })}
                                            error={validationErrors.billingCity}
                                            styles={{
                                                labelSx: {
                                                    fontSize: '0.75rem',
                                                },
                                            }}
                                        />
                                        <TextQuestionField
                                            field={{ id: 'billingZip', label: 'ZIP Code', placeholder: '10001', size: 'compact' }}
                                            value={checkout.billingZip}
                                            onChange={(v) => updateCheckout({ billingZip: String(v).replace(/\D/g, '').slice(0, 5) })}
                                            error={validationErrors.billingZip}
                                            styles={{
                                                labelSx: {
                                                    fontSize: '0.75rem',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <BasicSelectQuestionField
                                        field={{
                                            id: 'billingState',
                                            type: 'selectBasic',
                                            label: 'State',
                                            placeholder: 'Select state...',
                                            size: 'compact',
                                            options: STATE_OPTIONS_BY_NAME,
                                        }}
                                        value={checkout.billingState}
                                        onChange={(v) => updateCheckout({ billingState: v, billingStateCode: getStateCodeByName(v, '') })}
                                        error={validationErrors.billingState}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>

                    <Typography sx={{ mt: 2.2, fontSize: '0.82rem', color: ONLINE_CART_TOKENS.textMuted, lineHeight: 1.65 }}>
                        By clicking &quot;Pay now,&quot; you agree to PRIME&apos;s{' '}
                        <Link href={TERMS_AND_CONDITIONS} target="_blank" rel="noopener noreferrer" underline="always" sx={{ fontSize: '0.82rem', color: ONLINE_CART_TOKENS.primary }}>
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link href={PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" underline="always" sx={{ fontSize: '0.82rem', color: ONLINE_CART_TOKENS.primary }}>
                            Privacy Policy
                        </Link>
                        .
                    </Typography>

                    <ButtonBase
                        onClick={handleContinue}
                        disabled={paymentProcessing}
                        sx={{
                            mt: 2.2,
                            width: '100%',
                            height: 50,
                            borderRadius: 1.5,
                            bgcolor: ONLINE_CART_TOKENS.primary,
                            color: ONLINE_CART_TOKENS.surface,
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            '&:hover': { bgcolor: ONLINE_CART_TOKENS.primaryHover },
                            '&.Mui-disabled': {
                                opacity: 0.6,
                                bgcolor: ONLINE_CART_TOKENS.primary,
                                color: ONLINE_CART_TOKENS.surface,
                            },
                            '&:focus-visible': {
                                outline: `3px solid ${ONLINE_CART_TOKENS.ring}`,
                                outlineOffset: '2px',
                            },
                        }}
                    >
                        {paymentProcessing ? (paymentAlreadyProcessed ? 'Continuing...' : 'Processing payment...') : paymentAlreadyProcessed ? 'Continue' : `Pay now — ${formatPrice(total)}`}
                    </ButtonBase>
                    {paymentError ? <Typography sx={{ mt: 1, fontSize: '0.875rem', fontWeight: 500, color: ONLINE_CART_TOKENS.error }}>{paymentError}</Typography> : null}

                    <Box sx={{ mt: 1.5, display: 'grid', rowGap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockOutlinedIcon sx={{ fontSize: 16, color: ONLINE_CART_TOKENS.success }} />
                            <Typography sx={{ fontSize: '0.82rem', color: ONLINE_CART_TOKENS.textMuted }}>
                                <Typography component="span" sx={{ fontWeight: 700, color: ONLINE_CART_TOKENS.textSuccess, fontSize: 'inherit' }}>
                                    Secure checkout
                                </Typography>{' '}
                                - SSL Encrypted. 256-bit SSL encryption.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: ONLINE_CART_TOKENS.primary }} />
                            <Typography sx={{ fontSize: '0.82rem', color: ONLINE_CART_TOKENS.textMuted }}>
                                LLC formation costs are{' '}
                                <Typography component="span" sx={{ fontWeight: 700, color: ONLINE_CART_TOKENS.textBody, fontSize: 'inherit' }}>
                                    tax-deductible
                                </Typography>{' '}
                                business expenses.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ ...cardBorderStyle, order: { xs: 1, md: 2 }, alignSelf: 'start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: summaryOpen ? 1 : 0 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textHeading, display: { xs: 'block' }, mb: summaryOpen ? 2 : 0 }}>Order Summary</Typography>
                        <ButtonBase onClick={() => setSummaryOpen((s) => !s)} sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: ONLINE_CART_TOKENS.textMutedSoft }}>
                            {summaryOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </ButtonBase>
                    </Box>

                    {/* Order summary */}
                    <Collapse in={summaryOpen} sx={{ display: { xs: 'block', sm: 'block' } }}>
                        <Box sx={{ display: 'grid', rowGap: 1.25 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>{getEntityRenewalLabel('PRIME LLC Formation')}</Typography>
                                <PriceCell original={getEntityRenewalPrice()} discountPercent={productDiscountMap.get(ENTITY_RENEWAL_DISCOUNT_ID)} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>{`${stateLabel} Filing Fee`}</Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textHeading, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>
                                    {formatPrice(stateFee)}
                                </Typography>
                            </Box>

                            {hasDynamicProducts ? (
                                selectedProductLineItems.map(({ product, option, price, fieldId, declineValue, canRemove }) => (
                                    <Box key={product.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35 }}>
                                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>{product.productDisplayName || product.title || option.label}</Typography>
                                            {canRemove ? (
                                                <ButtonBase onClick={() => handleSelectDynamicProductOption(fieldId, declineValue)} sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.error }}>
                                                    Remove
                                                </ButtonBase>
                                            ) : null}
                                        </Box>
                                        <PriceCell original={price} originalDisplay={option.priceDisplay} discountPercent={productDiscountMap.get(String(product.id))} />
                                    </Box>
                                ))
                            ) : (
                                <>
                                    {isRegisteredAgentAdded ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35 }}>
                                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>Registered Agent Service (annual)</Typography>
                                                <ButtonBase onClick={() => handleToggleAddon('registeredAgent', false)} sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.error }}>
                                                    Remove
                                                </ButtonBase>
                                            </Box>
                                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textHeading, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>
                                                {formatPrice(99)}
                                            </Typography>
                                        </Box>
                                    ) : null}

                                    {isExpeditedAdded ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35 }}>
                                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>Expedited Processing</Typography>
                                                <ButtonBase onClick={() => handleToggleAddon('expeditedProcessing', false)} sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.error }}>
                                                    Remove
                                                </ButtonBase>
                                            </Box>
                                            <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textHeading, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>
                                                {formatPrice(49)}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </>
                            )}

                            {isRelayAdded ? (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35 }}>
                                        <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>Relay Business Bank Account</Typography>
                                        <ButtonBase onClick={() => handleToggleAddon('relayBank', false)} sx={{ fontSize: '0.78rem', color: ONLINE_CART_TOKENS.error }}>
                                            Remove
                                        </ButtonBase>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.success, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>Free</Typography>
                                </Box>
                            ) : null}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.textStrong }}>Complimentary Tax Strategy Consultation</Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: ONLINE_CART_TOKENS.success, fontWeight: 700, minWidth: '60px', textAlign: 'right' }}>Free</Typography>
                            </Box>

                            {hasDynamicProducts
                                ? addableDynamicProducts.map(({ product, fieldId, paidOptions }) => (
                                      <AddableProduct
                                          key={fieldId}
                                          productName={product.productDisplayName || product.title || 'this service'}
                                          paidOptions={paidOptions}
                                          fieldId={fieldId}
                                          onSelect={handleSelectDynamicProductOption}
                                      />
                                  ))
                                : null}

                            {!hasDynamicProducts && !isRegisteredAgentAdded ? (
                                <ButtonBase
                                    onClick={() => handleToggleAddon('registeredAgent', true)}
                                    sx={{
                                        mt: 0.5,
                                        p: 1.2,
                                        borderRadius: 1.5,
                                        border: `1px dashed ${ONLINE_CART_TOKENS.primaryBorderDashed}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        color: ONLINE_CART_TOKENS.primary,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    <span>+ Add Registered Agent</span>
                                    <span>$99/yr</span>
                                </ButtonBase>
                            ) : null}
                            {/* {!isRelayAdded ? (
                                <ButtonBase
                                    onClick={() => handleToggleAddon('relayBank', true)}
                                    sx={{
                                        p: 1.2,
                                        borderRadius: 1.5,
                                        border: `1px dashed ${ONLINE_CART_TOKENS.primaryBorderDashed}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        color: ONLINE_CART_TOKENS.primary,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    <span>+ Add Relay Business Bank Account</span>
                                    <span style={{ color: ONLINE_CART_TOKENS.success }}>Free</span>
                                </ButtonBase>
                            ) : null} */}
                            {!hasDynamicProducts && !isExpeditedAdded ? (
                                <ButtonBase
                                    onClick={() => handleToggleAddon('expeditedProcessing', true)}
                                    sx={{
                                        p: 1.2,
                                        borderRadius: 1.5,
                                        border: `1px dashed ${ONLINE_CART_TOKENS.primaryBorderDashed}`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        color: ONLINE_CART_TOKENS.primary,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    <span>+ Add Expedited Processing</span>
                                    <span>$49</span>
                                </ButtonBase>
                            ) : null}

                            <Box sx={{ pt: 1.25, borderTop: `1px solid ${ONLINE_CART_TOKENS.border}` }}>
                                {!checkout.promoApplied ? (
                                  <>
                                <ButtonBase
                                    onClick={() => setShowPromo((p) => !p)}
                                    sx={{
                                        color: ONLINE_CART_TOKENS.primaryAccent,
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontFamily: ONLINE_CART_TOKENS.fontFamily,
                                    }}
                                >
                                    Have a promo code?
                                    {showPromo ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                                </ButtonBase>
                                <Collapse in={showPromo}>
                                    <Box sx={{ mt: 1 }}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Enter code"
                                                value={checkout.promoCode}
                                                onChange={(event) => {
                                                    updateCheckout(getPromoResetPatch(event.target.value.toUpperCase()));
                                                    setPromoFeedback({ type: '', message: '' });
                                                }}
                                                sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 40, borderRadius: 1.5 } }}
                                            />
                                            <ButtonBase
                                                onClick={handleApplyPromo}
                                                disabled={promoApplying}
                                                sx={{
                                                    minWidth: 72,
                                                    height: 40,
                                                    px: 2,
                                                    borderRadius: 1.5,
                                                    border: `1px solid ${ONLINE_CART_TOKENS.border}`,
                                                    color: ONLINE_CART_TOKENS.textStrong,
                                                    fontSize: '0.92rem',
                                                    fontWeight: 600,
                                                    '&.Mui-disabled': {
                                                        opacity: 0.7,
                                                    },
                                                }}
                                            >
                                                {promoApplying ? 'Applying...' : 'Apply'}
                                            </ButtonBase>
                                        </Box>
                                        {promoFeedback.message ? (
                                            <Typography
                                                sx={{
                                                    mt: 0.9,
                                                    fontSize: '0.78rem',
                                                    fontWeight: 600,
                                                    color: promoFeedback.type === 'success' ? ONLINE_CART_TOKENS.textSuccess : ONLINE_CART_TOKENS.error,
                                                }}
                                            >
                                                {promoFeedback.message}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Collapse>
                                  </>
                                ) : (
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 1.25,
                                          p: 1.5,
                                          borderRadius: 1.5,
                                          backgroundColor: ONLINE_CART_TOKENS.successBgSoft,
                                          border: `1px solid ${ONLINE_CART_TOKENS.textSuccess}40`,
                                      }}
                                  >
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                          <CheckCircleOutlineIcon sx={{ fontSize: 22, color: ONLINE_CART_TOKENS.textSuccess, flexShrink: 0, mt: '2px' }} />
                                          <Box sx={{ minWidth: 0, flex: 1 }}>
                                              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textStrong, lineHeight: 1.3 }}>
                                                  {`Promo applied · ${checkout.promoCode}`}
                                              </Typography>
                                              <Typography sx={{ fontSize: '0.82rem', color: ONLINE_CART_TOKENS.textMuted, lineHeight: 1.4, mt: 0.25, wordBreak: 'break-word' }}>
                                                  {`You saved ${formatPrice(checkout.promoDiscount)}${checkout.promoDescription ? ` · ${checkout.promoDescription}` : ''}`}
                                              </Typography>
                                          </Box>
                                      </Box>
                                      <Box
                                          sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 1,
                                              pt: 1,
                                              borderTop: `1px solid ${ONLINE_CART_TOKENS.textSuccess}26`,
                                          }}
                                      >
                                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: ONLINE_CART_TOKENS.textSuccess }}>
                                              {formatPrice(checkout.promoDiscount)}
                                          </Typography>
                                          <ButtonBase
                                              onClick={handleRemovePromo}
                                              aria-label="Remove promo code"
                                              sx={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 0.4,
                                                  color: ONLINE_CART_TOKENS.error,
                                                  fontSize: '0.82rem',
                                                  fontWeight: 700,
                                                  px: 1.25,
                                                  py: 0.5,
                                                  borderRadius: 1,
                                                  border: `1px solid ${ONLINE_CART_TOKENS.error}59`,
                                                  '&:hover': { backgroundColor: '#FEECEC' },
                                              }}
                                          >
                                              <CloseIcon sx={{ fontSize: 15 }} />
                                              Remove
                                          </ButtonBase>
                                      </Box>
                                  </Box>
                                )}
                            </Box>

                            <Box sx={{ pt: 2, mt: 1, borderTop: `2px solid ${ONLINE_CART_TOKENS.textHeading}` }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography sx={{ fontSize: '1.125rem', lineHeight: 1.05, color: ONLINE_CART_TOKENS.textDisplay, fontWeight: 700 }}>Total due today</Typography>
                                    <Typography sx={{ fontSize: '1.5rem', lineHeight: 1, color: ONLINE_CART_TOKENS.textDisplay, fontWeight: 800 }}>{formatPrice(total)}</Typography>
                                </Box>
                                <Typography sx={{ mt: 1, fontSize: '0.75rem', color: ONLINE_CART_TOKENS.textMuted }}>
                                    Annual renewal: {formatPrice(getEntityRenewalPrice())}/yr + state fees{isRegisteredAgentAdded ? ' + $99/yr Registered Agent' : ''}
                                </Typography>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Box>

            {error && FieldError ? <FieldError error={error} /> : null}
        </Box>
    );
}
